import { useOverlay } from '@uhg-abyss/web/hooks/useOverlay';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { PageBody } from '@uhg-abyss/web/ui/PageBody';
import find from 'lodash/find';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  NEW_TAB_CONFIRMATION,
  PRACTITIONER_NAME_FOR_ANALYTICS,
  ProviderTypeMapFF,
  ReverseCoverageTypesCodes,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { mobileOnly } from '../../../../common/ConstantsStyles';
import { CustomAttributesBlock } from '../../../../common/ContentFilters/InfoModal';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { PSXPrintWrapper } from '../../../../common/PSXPrintWrapper/PSXPrintWrapper';
import { getFeatureFlag } from '../../../../common/Utils';
import {
  appendCoverageTypes,
  convertProviderTypeToAdobeType,
  formatProviderId,
  handleLinkAndModalTrack,
} from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import {
  defaultStringToEmpty,
  getAcoBenefitPlan,
} from '../../../../common/Widget/SmartChoiceWidget/utils';
import { PortalContext } from '../../../../context/PortalContext';
import { useAdobePageTrackEvent } from '../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { createLlmFlagTransformer } from '../../../../hooks/useCategoryLLMFlag/useCategoryLLMFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { usePrintPSX } from '../../../../hooks/usePSXPrint/usePrintPSX';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { Provider } from '../../../../models/Provider';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../../store/useDetailsStore';
import { DetailsStore } from '../../../../store/useDetailsStore/detailsStore';
import { isSuppressPremiumSmartchoice } from '../../../../utils/smartChoice.utils';
import {
  getCurrentMember,
  getMarketType,
  getProductCodeForCoverageType,
} from '../../../../utils/user.utils';
import { ConfirmationModal } from '../MapView/CompareDrawer/Components/ConfirmationModal';
import {
  BENEFIT_ATTRIBUTES,
  CONVENIENCE_ATTRIBUTES,
  PERSONAL_FIT_ATTRIBUTES,
  QUALITY_ATTRIBUTES,
  noAttributesDisplayed,
} from '../MapView/CompareDrawer/utility/compareDrawerConstants';
import { openTabOrNavigateOnClick } from '../MapView/CompareDrawer/utility/handleDetailsOnClick';
import { PrintCompareShareDrawer } from '../MapView/PrintMapView/PrintCompareShareDrawer';
import {
  hideAskAIWidget,
  hideChat,
  hideYourFeedbackForm,
  hideYourOpinionMattersPopup,
} from '../MapView/ShareDrawer/utils';
import { isGuestWithPlan } from '../MapView/utils';
import { Benefits } from './Benefits';
import { CompareFooter } from './CompareExpandedFooter';
import { CompareHeader } from './CompareExpandedHeader';
import { CompareSmartChoiceHeader } from './CompareHeader';
import {
  DividerStyle,
  EditCompareAndPrintButton,
  SectionBackgroundStyled,
  SectionHeadingStyled,
} from './CompareSmartChoice.styled';
import { Convenience } from './Convenience';
import { PersonalFit } from './PersonalFit';
import { Quality } from './Quality';

const StyledButton = styled(Button, {
  '&.abyss-button-root': {
    padding: '0px',
  },
});

const defaultBooleanToFalse = (bool?: boolean | null): boolean => bool ?? false;

const getIsCostIndicator = (
  tired: boolean,
  aco: boolean,
  indicator: any
): boolean =>
  !tired &&
  !aco &&
  (indicator === '$' || indicator === '$$' || indicator === '$$$');

const shouldDisplayTitle = (
  selectedAttributes: any,
  attributesToCheck: string[]
) =>
  selectedAttributes?.some(
    (item: any) => item?.isChecked && attributesToCheck.includes(item?.name)
  );

const SectionHeading = ({ heading, selectedAttributes, attr }) => (
  <Layout.Stack
    data-testid={`compare-${heading
      .replace(/\s+/g, '-')
      .toLowerCase()}-heading`}
    grow
  >
    {shouldDisplayTitle(selectedAttributes, attr) && (
      <SectionBackgroundStyled>
        <SectionHeadingStyled>{heading}</SectionHeadingStyled>
      </SectionBackgroundStyled>
    )}
  </Layout.Stack>
);

const CompareSmartChoiceToolBar = ({
  t,
  setIsEditCompareOpenSmartChoice,
  editCompareButtonRef,
  printPSXPage,
}) => {
  const editCompareButtonTestId = 'edit-compare-button-smart-choice';
  const lagoonData = useLagoon('ui-messaging')();
  const edit_button = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_EDIT_COMPARISON,
  })?.message;
  return (
    <React.Fragment>
      <EditCompareAndPrintButton
        alignItems="center"
        css={{ flexWrap: 'nowrap', padding: '0px 8px', margin: '0px -16px' }}
        justify="space-between"
      >
        <StyledButton
          data-auto-testid={editCompareButtonTestId}
          data-testid={editCompareButtonTestId}
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Edit comparison features',
              location: 'Compare providers successful',
              type: 'Internal',
            });
            setIsEditCompareOpenSmartChoice(true);
          }}
          ref={editCompareButtonRef}
          role="button"
          variant="text"
        >
          <IconSymbol icon="edit" />
          {edit_button}
        </StyledButton>
        <StyledButton
          css={{
            '@media (max-width: 744px)': {
              marginRight: '-5px',
              '& .print-text': {
                display: 'none', // Hide the text for small devices
              },
            },
          }}
          data-auto-testid="mapview-print-button"
          data-testid="mapview-print-button"
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Print & Share',
              location: 'Compare providers successful',
              type: 'Internal',
            });
            hideChat(true);
            hideAskAIWidget(true);
            hideYourFeedbackForm(true);
            hideYourOpinionMattersPopup(true);
            printPSXPage();
            hideChat(false);
            hideAskAIWidget(false);
            hideYourFeedbackForm(false);
            hideYourOpinionMattersPopup(false);
          }}
          variant="text"
        >
          <IconSymbol icon="print" />
          <span className="print-text">
            {t('SHARE_DRAWER_TEXT.PrintAndShare')}
          </span>
        </StyledButton>
      </EditCompareAndPrintButton>
    </React.Fragment>
  );
};

export const CompareSmartChoice = ({
  selectedAttributes,
  selectedProviders,
  removeItem,
  setCompareClicked,
  setIsEditCompareOpenSmartChoice,
  headers,
  setItems,
  isTieredPlan,
  choosePCP = false,
  setIsPickProviderModalOpen,
  setPickProviderIndex,
  recommendationPreferences,
  editCompareButtonRef,
}) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { printPSXPage, onPrintReady } = usePrintPSX();
  const [searchParams] = useSearchParams();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const { chipValue, coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      chipValue: state.chipValue,
    }))
  );

  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'compare providers',
    sitesectionLevel1: 'results details',
    sitesectionLevel2: 'provider details',
  });

  const preferencesData = useStore(StoreKeys.RECOMMENDATION_PREFERENCES);

  const displayInNetworkBadgeEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_IN_NETWORK_BADGE_FUTURE_START_DATE
  );

  const currentMember = getCurrentMember();
  const showTier2Badge: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_TWO_PROVIDER
  );
  const enableLLMMedicalSpecialists: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LLM_MEDICAL_SPECIALISTS
  );
  const llmSpecialityFilterToggle: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.LLM_SPECIALITY_FILTER_TOGGLE
  );
  const invokeLLMModel = (careType: string, providerType: string): boolean => {
    const flagKey = `ENABLE_LLM_${defaultStringToEmpty(
      careType?.toUpperCase()?.replace(/\s+/g, '_')
    )}_${ProviderTypeMapFF[providerType]}`;
    return getFeatureFlag(
      featureFlags,
      createLlmFlagTransformer(llmSpecialityFilterToggle)(flagKey)
    );
  };

  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;
  const isTier2Plan = currentMember?.isIFPTieredPlan && showTier2Badge;

  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    adobePageTrackEvent();
  }, []);
  const topAnchorRef = useRef<HTMLDivElement>(null);

  const notExpandedCardsScreen = useMediaQuery(mobileOnly);

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );
  const modalName = 'view-provider-modal';
  const modal = useOverlay(modalName);

  const customAttributesBlock: CustomAttributesBlock = {
    correlationId: headers?.correlationId,
    providerId: formatProviderId(
      appendCoverageTypes(selectedProvider, coverageType)
    ),
    providerType: convertProviderTypeToAdobeType(
      selectedProvider?.providerType
    ),
  };
  const { portalData } = React.useContext(PortalContext);
  const isGuestUser = portalData
    ? portalData?.portalConfig?.isGuestUser
    : false;
  const url = useMemo(() => window.location.href, []);
  const guestPlan = useMemo(() => isGuestWithPlan(url), [url]);

  const handleDetailsOnClickCb = (
    providerId,
    options,
    customAttributesBlock: CustomAttributesBlock
  ) => {
    setSelectedProvider(null);
    openTabOrNavigateOnClick({
      providerId,
      navigate,
      options,
      chipStoreValues: { chipValue, coverageType },
      setDetailsStore,
      customAttributesBlock,
      isTieredPlan,
      providerTierDesignation:
        selectedProvider?.recommendationDetails?.benefits
          ?.providerTierDesignation,
      llmStatus: selectedProvider?.recommendationDetails?.quality?.llmStatus,
      isGuestUser,
      isGuestWithPlan: guestPlan,
    });
  };

  const handleClickCb = () => {
    modal?.close();
    const detailsStoreValues = {
      specialtySearch: selectedProvider?.speciality,
    };
    setDetailsStore(detailsStoreValues);
    handleDetailsOnClickCb(
      selectedProvider?.providerId,
      {
        openInNewTab: true,
        providerType: selectedProvider?.providerType,
        suppressPremium: recommendationPreferences?.suppressPremium,
        suppressCostEfficiency:
          recommendationPreferences?.suppressCostEfficiency,
        recommendationRadius: recommendationPreferences?.recommendationRadius,
      },
      customAttributesBlock
    );
  };

  const onProviderClickHandler = (provider: Provider) => {
    handleLinkAndModalTrack(
      PRACTITIONER_NAME_FOR_ANALYTICS,
      `modal:compare card:position`,
      NEW_TAB_CONFIRMATION,
      undefined,
      customAttributesBlock
    );
    modal?.open();
    setSelectedProvider(provider);
  };

  const allConfigs = useLagoon('config')();

  const selectedMember = getCurrentMember();

  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;
  const isAcoBenefitPlan = getAcoBenefitPlan(
    defaultStringToEmpty(
      getProductCodeForCoverageType(
        selectedMember,
        ReverseCoverageTypesCodes.MEDICAL
      )
    ),
    defaultStringToEmpty(getMarketType(selectedMember)),
    acoBenefitPlanCollection
  );

  const premiumSuppressionState = allConfigs?.find(
    (row) => row.key === 'SMART_CHOICE_SUPRESS_PREMIUM'
  )?.value;

  const { stateCode } = getGeoLocationFromStorage();
  const isPremiumCareSuppressed = isSuppressPremiumSmartchoice(
    premiumSuppressionState,
    stateCode
  );

  const scrollToTop = () => {
    topAnchorRef.current?.scrollIntoView({
      behavior: 'auto',
      block: 'start',
      inline: 'nearest',
    });

    let scrollParent: HTMLElement | null | undefined =
      topAnchorRef.current?.parentElement;
    while (scrollParent) {
      const { overflowY } = window.getComputedStyle(scrollParent);
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        scrollParent.scrollHeight > scrollParent.clientHeight
      ) {
        scrollParent.scrollTop = 0;
      }
      scrollParent = scrollParent.parentElement;
    }

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ left: 0, top: 0 });
  };

  useEffect(() => {
    // Repeat across frames to handle delayed content/layout updates.
    scrollToTop();
    if (isSmartChoice) {
      let rules = {
        hasPCPServiceAvailability: false,
        specialties: false,
        premiumCareProvider: false,
        credentials: false,
        serviceData: false,
        networkStatus: false,
        tier: false,
        acoAffiliation: false,
        greenDiamond: false,
        costIndicator: false,
        costEfficiency: false,
        acceptingNewPatients: false,
        appointmentAvailability: false,
        hasEveningAppointments: false,
        hasWeekendAppointment: false,
        onlineAppointmentScheduling: false,
        virtualCareOffered: false,
        distance: false,
        languageSpoken: false,
        gender: false,
        boardCertified: false,
        reviews: false,
        ethnicity: false,
        race: false,
      };
      const preferredLanguage = preferencesData?.languagesSpoken?.toLowerCase();
      const preferredGender = preferencesData?.providerGender?.toLowerCase();
      const preferredBoardCert =
        preferencesData?.providerBoardCertStatus?.toLowerCase();

      selectedProviders?.forEach((provider) => {
        const isACOProvider =
          provider?.acoProvider?.length > 0 &&
          !defaultBooleanToFalse(isSmartChoice);

        const { benefits, quality, convenience } =
          provider?.recommendationDetails;
        const currentValue = {
          hasPCPServiceAvailability:
            provider?.hasPCPServiceAvailability ||
            rules.hasPCPServiceAvailability,
          specialties: provider?.speciality !== '' || rules?.specialties,
          // quality attributes
          credentials:
            quality?.credentialed?.toLowerCase() === 'y' || rules?.credentials,
          premiumCareProvider:
            quality?.premiumDesignation !== 'NA' &&
            (isPremiumCareSuppressed === 'N' || rules?.premiumCareProvider),
          serviceData:
            (enableLLMMedicalSpecialists &&
              invokeLLMModel(
                searchParams?.get('chipValue') ?? '',
                provider?.providerType
              ) &&
              quality?.llmStatus !== 'NA') ||
            rules?.serviceData,
          // benefits
          networkStatus: !!provider?.networkStatus || rules?.networkStatus,
          tier:
            (!provider?.isGreenDiamondProvider &&
              isTieredPlan &&
              (benefits?.providerTierDesignation?.toLowerCase() === 'y' ||
                benefits?.providerTierDesignation?.toLowerCase() === 'n')) ||
            rules?.tier,
          greenDiamond:
            (provider?.isGreenDiamondProvider ?? false) || rules?.greenDiamond,
          acoAffiliation:
            (!provider?.isGreenDiamondProvider && isAcoBenefitPlan) ||
            rules?.acoAffiliation,
          costIndicator:
            getIsCostIndicator(
              isTieredPlan,
              isACOProvider,
              provider?.costIndicator
            ) || rules?.costIndicator,
          costEfficiency:
            benefits?.costEfficiencyRating !== 'NA' &&
            (benefits?.costEfficiencyRating !== '' || rules?.costEfficiency),
          // convenience attributes
          acceptingNewPatients:
            provider?.acceptingNewPatients || rules?.acceptingNewPatients,
          appointmentAvailability:
            (provider?.hoursOfOperation &&
              Object.keys(provider?.hoursOfOperation)?.length > 1) ||
            rules?.appointmentAvailability,
          hasEveningAppointments:
            preferencesData?.evngApptAvbl === 'y' ||
            rules?.hasEveningAppointments,
          hasWeekendAppointment:
            preferencesData?.wkndApptAvbl === 'y' ||
            rules?.hasWeekendAppointment,
          onlineAppointmentScheduling:
            preferencesData?.onlineApptSchdlAvbl?.toLowerCase() === 'y' ||
            rules?.onlineAppointmentScheduling,
          virtualCareOffered:
            preferencesData?.virtualApptAvbl === 'y' ||
            rules?.virtualCareOffered,
          distance:
            preferencesData?.distance === '' ||
            convenience?.memberPreferredRadius === 'Y' ||
            rules?.distance,
          // personal fit attributes
          languageSpoken: preferredLanguage !== '' || rules?.languageSpoken,
          gender: preferredGender !== '' || rules?.gender,
          boardCertified: preferredBoardCert === 'y' || rules?.boardCertified,
          reviews: !!provider?.healthGradeRating || rules?.reviews,
          ethnicity:
            (provider?.providerEthnicity &&
              Object.values(provider?.providerEthnicity)?.length > 1) ||
            rules?.ethnicity,
          race:
            (provider?.providerRace &&
              Object.values(provider?.providerRace)?.length > 1) ||
            rules?.race,
        };
        rules = currentValue;
      });

      setItems((prevItems) => {
        const updatedItems = prevItems.map((item) => {
          item.isDisabled = !rules[item?.name] || false;
          item.isChecked = rules[item?.name] ?? item?.isChecked;
          if (item?.name === 'hasPCPServiceAvailability') {
            item.isChecked = choosePCP && rules?.hasPCPServiceAvailability;
          }
          return item;
        });

        return updatedItems;
      });
    } else {
      setItems((prevItems) => {
        const updatedItems = prevItems.map((item) => {
          item.isDisabled = false;
          item.isChecked = true;
          if (item?.name === 'hasPCPServiceAvailability') {
            item.isChecked = choosePCP ? true : false;
          }
          return item;
        });
        return updatedItems;
      });
    }
  }, []);

  const filteredProviders = notExpandedCardsScreen
    ? selectedProviders.slice(0, 2)
    : selectedProviders;

  return (
    <PageBody>
      <div ref={topAnchorRef} />
      <Box color="white">
        <CompareSmartChoiceHeader
          handleBackButtonClick={() => setCompareClicked(false)}
        />
        <Box color="white">
          <CompareSmartChoiceToolBar
            editCompareButtonRef={editCompareButtonRef}
            printPSXPage={printPSXPage}
            setIsEditCompareOpenSmartChoice={setIsEditCompareOpenSmartChoice}
            t={t}
          />
        </Box>
        <DividerStyle />
        <>
          <CompareHeader
            filteredProviders={filteredProviders}
            imgRef={imgRef}
            isChangeBtnVisible={
              notExpandedCardsScreen && selectedProviders?.length > 2
            }
            onProviderClickHandler={onProviderClickHandler}
            selectedAttributes={selectedAttributes}
            selectedProviders={filteredProviders?.length}
            setIsPickProviderModalOpen={setIsPickProviderModalOpen}
            setPickProviderIndex={setPickProviderIndex}
          />
          {isSmartChoice && (
            <SectionHeading
              attr={QUALITY_ATTRIBUTES}
              heading={t('COMPARE.Quality')}
              selectedAttributes={selectedAttributes}
            />
          )}
          {!noAttributesDisplayed(selectedAttributes, QUALITY_ATTRIBUTES) && (
            <Quality
              filteredProviders={filteredProviders}
              isPremiumCareSuppressed={isPremiumCareSuppressed}
              isSmartChoice={isSmartChoice}
              selectedAttributes={selectedAttributes}
            />
          )}

          {isSmartChoice && (
            <SectionHeading
              attr={BENEFIT_ATTRIBUTES}
              heading={t('COMPARE.Benefits')}
              selectedAttributes={selectedAttributes}
            />
          )}
          {!noAttributesDisplayed(selectedAttributes, BENEFIT_ATTRIBUTES) && (
            <Benefits
              displayInNetworkBadgeEnabled={displayInNetworkBadgeEnabled}
              filteredProviders={filteredProviders}
              isSmartChoice={isSmartChoice}
              isTier2Plan={isTier2Plan}
              selectedAttributes={selectedAttributes}
              selectedProviders={selectedProviders}
            />
          )}

          {isSmartChoice && (
            <SectionHeading
              attr={CONVENIENCE_ATTRIBUTES}
              heading={t('COMPARE.Convenience')}
              selectedAttributes={selectedAttributes}
            />
          )}
          {!noAttributesDisplayed(
            selectedAttributes,
            CONVENIENCE_ATTRIBUTES
          ) && (
            <Convenience
              filteredProviders={filteredProviders}
              preferencesData={preferencesData}
              selectedAttributes={selectedAttributes}
            />
          )}
          {isSmartChoice && (
            <SectionHeading
              attr={PERSONAL_FIT_ATTRIBUTES}
              heading={t('COMPARE.Personal_Fit')}
              selectedAttributes={selectedAttributes}
            />
          )}
          {!noAttributesDisplayed(
            selectedAttributes,
            PERSONAL_FIT_ATTRIBUTES
          ) && (
            <>
              <PersonalFit
                filteredProviders={filteredProviders}
                isSmartChoice={isSmartChoice}
                preferencesData={preferencesData}
                selectedAttributes={selectedAttributes}
              />
              <CompareFooter
                filteredProviders={filteredProviders}
                removeButtonText={t('SHARE_DRAWER_TEXT.REMOVE')}
                removeItem={removeItem}
              />
            </>
          )}
        </>
      </Box>

      <ConfirmationModal
        closeButtonText={
          t('COMPARE_DRAWER_CONFIRMATION_MODAL.VIEW_PROVIDER_BUTTON') || ''
        }
        dataForAnalytics={{
          linkName: PRACTITIONER_NAME_FOR_ANALYTICS,
          linkLocation: `modal:compare card:position`,
          modalName: NEW_TAB_CONFIRMATION,
          linkType: undefined,
          customAttributesBlock,
        }}
        handleClickCb={handleClickCb}
        isViewProvider
        locationForAnalytics={`modal:${NEW_TAB_CONFIRMATION}`}
        modal={modal}
        modalName={modalName}
      />

      <PSXPrintWrapper onReady={onPrintReady}>
        <PrintCompareShareDrawer
          preferencesData={preferencesData}
          selectedAttributes={selectedAttributes}
          selectedProviders={selectedProviders}
        />
      </PSXPrintWrapper>
    </PageBody>
  );
};
